import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ReactComponent as ZocksLogo } from '../../../assets/ZocksLogo.svg'
import { AlwaysScrollToBottom } from '../../../components/AlwaysScrollToBottom/AlwaysScrollToBottom'
import { MarkdownViewer } from '../../AIAgent/Chat/MarkdownViewer'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { AIAgentType } from '../../../models/AIAgent'
import {
  addMessage,
  clearChat,
  selectMessagesForStreaming,
  selectStreamingError,
  setMessages,
  setStreamingError,
  updateStreamingMessage,
} from '../../../store/streamingChat/streamingChat'
import { useStreamingChat } from '../../AIAgent/hooks/useStreamingChat'
import { useExecuteAiAgentStreamingMutation, useInteractWithAiAgentStreamingMutation } from '../../../store/ai/aiAgent'
import { AddSampleModal } from './AddSampleModal'
import { TemplateChatInputBar } from './TemplateChatInputBar'
import { Button } from '../../../components/Button/Button'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { TemplateGeneratorSource } from './tracking'

interface TemplateGeneratorResult {
  message: string
  template?: string
}

interface TemplateAIChatProps {
  templateId: string
  currentTemplateRef: React.RefObject<string | null>
  onTemplateUpdate: (template: string) => void
  onLoadingChange?: (loading: boolean) => void
  readOnly?: boolean
  initialAiAgentId?: string | null
  initialMessages?: Array<{ type: 'user' | 'agent'; message: string }>
}

export const TemplateAIChat: React.FC<TemplateAIChatProps> = ({
  templateId,
  currentTemplateRef,
  onTemplateUpdate,
  onLoadingChange,
  readOnly,
  initialAiAgentId,
  initialMessages,
}) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const { tracked } = useTracking()
  const messages = useAppSelector(selectMessagesForStreaming)
  const errorData = useAppSelector(selectStreamingError)

  const [aiAgentId, setAiAgentId] = useState<string | null>(initialAiAgentId ?? null)
  const [isLoading, setIsLoading] = useState(false)
  const [chatMessage, setChatMessage] = useState('')
  const [showAddSampleModal, setShowAddSampleModal] = useState(false)
  const [addedSample, setAddedSample] = useState<string>('')

  const latestTemplateRef = useRef<string | null>(null)

  const [triggerExecuteStreaming] = useExecuteAiAgentStreamingMutation()
  const [triggerInteractStreaming] = useInteractWithAiAgentStreamingMutation()

  const onTextDelta = useCallback(
    (data: TemplateGeneratorResult) => {
      const { message, template } = data
      if (message) {
        dispatch(updateStreamingMessage(message))
      }
      if (template) {
        latestTemplateRef.current = template
      }
    },
    [dispatch],
  )

  const onNewAiAgentId = useCallback((newId: string) => {
    setAiAgentId(newId)
  }, [])

  const { onStreamingEvent } = useStreamingChat<TemplateGeneratorResult>({ onTextDelta, onNewAiAgentId })

  const [initialMessagesState] = useState(initialMessages)

  useEffect(() => {
    if (initialMessagesState?.length) {
      dispatch(setMessages(initialMessagesState))
    }
    return () => {
      dispatch(clearChat())
    }
  }, [dispatch, initialMessagesState])

  const _executeStreaming = async (message: string, sample?: string) => {
    await triggerExecuteStreaming({
      accountId,
      payload: {
        type: AIAgentType.TEMPLATE_GENERATOR,
        context: {
          templateId,
          template: currentTemplateRef.current ?? '',
          message,
          ...(sample ? { example: sample } : {}),
        },
      },
      onStreamingEvent,
    })
  }

  const executeStreaming = tracked(
    _executeStreaming,
    { eventName: TrackEvent.TEMPLATE_GENERATOR_EXECUTED },
    {
      eventDetailsExtractor: (sample) => ({
        templateId,
        sampleAttached: !!sample,
        source: TemplateGeneratorSource.TemplateAIChat,
      }),
    },
  )

  const _interactStreaming = async (message: string, sample?: string) => {
    await triggerInteractStreaming({
      accountId,
      aiAgentId: aiAgentId!,
      payload: {
        message,
        template: currentTemplateRef.current ?? '',
        ...(sample ? { example: sample } : {}),
      },
      onStreamingEvent,
    })
  }

  const interactStreaming = tracked(
    _interactStreaming,
    { eventName: TrackEvent.TEMPLATE_GENERATOR_INTERACTED_WITH },
    {
      eventDetailsExtractor: (sample) => ({
        templateId,
        sampleAttached: !!sample,
        source: TemplateGeneratorSource.TemplateAIChat,
      }),
    },
  )

  const handleSubmit = async (message: string, sample?: string) => {
    if (!message.trim() || isLoading) {
      return
    }

    setIsLoading(true)
    onLoadingChange?.(true)
    dispatch(addMessage({ type: 'user', message }))
    dispatch(setStreamingError(null))

    try {
      if (!aiAgentId) {
        await executeStreaming(message, sample)
      } else {
        await interactStreaming(message, sample)
      }

      if (latestTemplateRef.current) {
        onTemplateUpdate(latestTemplateRef.current)
        latestTemplateRef.current = null
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
      onLoadingChange?.(false)
    }
  }

  const handleSendMessage = async () => {
    const message = chatMessage.trim()
    if (!message) {
      return
    }
    const sample = addedSample
    setChatMessage('')
    await handleSubmit(message, sample || undefined)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSendMessage()
    }
  }

  const handleAddSample = (sample: string) => {
    setAddedSample(sample)
    setShowAddSampleModal(false)
    if (!chatMessage.trim()) {
      setChatMessage('Generate a template based on the provided example')
    }
  }

  const handleResubmit = async () => {
    const lastUserMessage = [...messages].reverse().find((m) => m.type === 'user')
    if (!lastUserMessage) {
      return
    }
    dispatch(setStreamingError(null))
    await handleSubmit(lastUserMessage.message)
  }

  const showEmptyState = messages.length === 0

  return (
    <>
      <AddSampleModal
        show={showAddSampleModal}
        onCancel={() => setShowAddSampleModal(false)}
        onAddSample={handleAddSample}
        initialValue={addedSample || undefined}
      />

      <div className='d-flex flex-column border zocks-border-0.5 rounded-2 zocks-bg-color-white overflow-hidden'>
        <div className='d-flex align-items-center gap-3 px-4 py-3 border-bottom zocks-border-color-gray-200'>
          <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-header-logo zocks-border-0.25'>
            <ZocksLogo width={18} height={18} />
          </div>
          <div className='d-flex flex-column'>
            <span className='paragraph-small bold'>ZOCKS</span>
            <span className='paragraph-small'>AI Assistant</span>
          </div>
        </div>

        <div className='d-flex flex-column flex-grow-1 p-3'>
          {showEmptyState ? (
            <div className='d-flex flex-column align-items-center justify-content-center text-center gap-2 py-5 px-4 flex-grow-1'>
              <p className='paragraph-medium fw-bold zocks-text-gray-900 mb-0'>What would you like to change?</p>
              <p className='paragraph-medium zocks-text-gray-900 mb-0'>
                Add a section, adjust the tone, reorder content, or add a sample to reshape the template.
              </p>
            </div>
          ) : (
            <div className='d-flex flex-column zocks-gap-1.25 ai-agent-chat-messages flex-grow-1'>
              {messages.map((message, idx) => {
                if ((idx === 0 && message.type === 'agent') || (!message.message && !message.toolUses?.length)) {
                  return null
                }
                if (message.type === 'user') {
                  return (
                    <div key={idx} className='d-flex px-2 justify-content-end'>
                      <div className='zocks-p-0.75 ai-agent-chat-message zocks-border-0.5 zocks-bg-color-primary-light'>{message.message}</div>
                    </div>
                  )
                }
                const isPreviousMessageUser = idx > 0 && messages[idx - 1].type === 'user'
                return (
                  <div key={idx} className='d-flex flex-column zocks-gap-0.5 px-2'>
                    <div className='d-flex zocks-gap-0.75'>
                      {isPreviousMessageUser ? (
                        <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25'>
                          <ZocksLogo width={12} height={12} />
                        </div>
                      ) : (
                        <div className='ai-agent-chat-message-spacer' />
                      )}
                      <div className='d-flex flex-grow-1 flex-column gap-3'>
                        {message.message ? (
                          <div className='ai-agent-chat-message paragraph-medium'>
                            <MarkdownViewer markdown={message.message} />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )
              })}
              {errorData ? (
                <div className='d-flex flex-column gap-2 px-2'>
                  <div className='border border-danger-subtle zocks-border-0.75 p-3 zocks-bg-color-danger-background'>
                    <div className='zocks-text-danger-200'>Something happened. Please try again.</div>
                  </div>
                  <Button
                    className='button-small align-self-start p-1'
                    variant='primaryGhost'
                    loading={isLoading}
                    onClick={() => {
                      dispatch(setStreamingError(null))
                      void handleResubmit()
                    }}
                  >
                    Retry
                  </Button>
                </div>
              ) : null}
              {isLoading ? (
                <div className='d-flex zocks-gap-0.75 px-2'>
                  <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25'>
                    <ZocksLogo width={12} height={12} />
                  </div>
                  <span className='paragraph-medium zocks-text-gray-500 m-0 shimmering-loader-text'>Thinking…</span>
                </div>
              ) : null}
              <AlwaysScrollToBottom dependencies={[messages, errorData]} />
            </div>
          )}
        </div>

        <div className='px-3 pb-3'>
          <TemplateChatInputBar
            className='bg-white zocks-border-color-gray-300'
            value={chatMessage}
            onChange={setChatMessage}
            onSend={() => void handleSendMessage()}
            onKeyDown={handleKeyDown}
            addedSample={addedSample}
            onAddSampleClick={() => setShowAddSampleModal(true)}
            onRemoveSample={() => {
              setAddedSample('')
              if (chatMessage === 'Generate a template based on the provided example') {
                setChatMessage('')
              }
            }}
            isLoading={isLoading}
            disabled={readOnly}
            placeholder='Describe what to change...'
          />
        </div>
      </div>
    </>
  )
}
